import React, { useEffect, useRef, memo } from 'react';
import { resolveVisibility } from "../../../../common/model/condition/evaluate"
import processDefaults from '../../utils/defaults/processDefaults'

//Get current Country from Profile and use Valtio to read a snapshot
import useProfile from "../../hooks/useProfile";
import { snapshot } from "valtio";
import useSectionState from '../../hooks/useSectionState';

//Importing UI Components
import { Form } from '../form/FormComponents'
import CardViewFormSection from './CardViewFormSection'
import CardViewFormLines from './CardViewFormLines';
import CardViewFormTotals from './CardViewFormTotals';
import CardViewFormGrid from './CardViewFormGrid';
import CardViewFormExtGrid from './CardViewFormExtGrid';
import MobilePanel from '../mobilePanel/MobilePanel';
import Tabs from '../tabs/Tabs';

import runFieldsLogic from '../../../../common/logic/runFieldsLogic';

import { useStore } from '../pageView/Page';
import cardExtensions from './cardExtensions';
import { NSERIE_ADVANCED } from '../../events/sse/nSerieManager';
import { onCardCollapse } from '../../events/collapseCard';

// The fields a new document takes its number from a series for.
const nSerieFields = (sections = []) => sections.flatMap(section =>
    (section.groups || []).flatMap(g => g.type === 'group' ? (g.fields || []) : [g])
).filter(f => f?.defaultType === 'nserie' && f.id && f.entity)

const CardViewForm = memo(({ config, options, dispatch = () => {}, handleActions, validated }) => {
    let sections = config.sections
    const entity = config.entity

    const { pState } = useProfile()
    let profile = snapshot(pState)
    const [state, setState, getState] = useStore((s) => s)
    const prevData = { ...state }

    const mode = options.mode
    const load = options.load

    // The number each series field was offered, so a later one replaces it only
    // while the user has not typed a number of their own.
    const offeredNumbers = useRef({})

    const applyFieldsLogic = async () => {
        //ON NEW TRIGGER: ensure defaults are added to Data array and field logic is executed.
        if (!load && state.loaded) {
            if (!state.defaulted) {
                // console.log('Profile is: ', profile)
                let updatedProfile = {}
                updatedProfile = { ...profile }
                //Just injects the correct nSerie if available
                if (state?.settings?.nSeries) {
                    updatedProfile.profile = {}
                    updatedProfile.profile.nSeries = state.settings.nSeries
                }
                const defaultData = processDefaults(config.sections, updatedProfile, config.defaultData)
                for (const field of nSerieFields(config.sections)) {
                    offeredNumbers.current[field.id] = defaultData[field.id]
                }
                // Await the async function
                const processedData = await runFieldsLogic({ ...state, ...defaultData }, config.entity, config.fieldsLogic, prevData)
                // Only update state after the Promise resolves
                setState({ ...processedData })
            }
        }
    }

    /*
     * A booking elsewhere took a number from a series this new document uses
     * (nserie.advanced, 37.03). If the field still shows the number it was
     * offered, it moves on to the series' next one; a number the user typed
     * is theirs and stays. Booking is still what guarantees the number is free.
     */
    useEffect(() => {
        if (load) return
        const fields = nSerieFields(config.sections)
        if (fields.length === 0) return
        const onAdvanced = (event) => {
            const { entity: seriesEntity, nextValue } = event.detail || {}
            const current = getState()
            for (const field of fields) {
                if (field.entity !== seriesEntity) continue
                const offered = offeredNumbers.current[field.id]
                if (!offered || current[field.id] !== offered || offered === nextValue) continue
                offeredNumbers.current[field.id] = nextValue
                setState({ [field.id]: nextValue })
            }
        }
        window.addEventListener(NSERIE_ADVANCED, onAdvanced)
        return () => window.removeEventListener(NSERIE_ADVANCED, onAdvanced)
    }, [load, config.sections])

    //Getting the State
    useEffect(() => {
        //Checks if the state has been defaulted if not tries to apply fieldsLogic again.
        if (!state.defaulted) {
            applyFieldsLogic()
        }
    }, [state]
    )

    const { expanded, toggleExpanded, collapseAll } = useSectionState(config, options)

    // The whole form to its displays when asked (collapseCard) — the document
    // suggestion panel after Apply. Only the card form: a side panel sharing
    // the entity (the document viewer beside an expense) stays as it is.
    useEffect(() => onCardCollapse(entity, collapseAll), [entity])



    const handleSubmit = async (e) => {
        e.preventDefault()
        const actionResponse = await handleActions({ action: 'save' })
    }


    const localDispatch = async (action, data) => {
        if (action === 'saveNContinue' || action === 'save') {
            const actionResponse = await handleActions({ action: 'save', invalidateQuery: data?.invalidateQuery })
            dispatch(action, data)
        } else {
            dispatch(action, data)
        }
    }

    //Configure the form depending on the existence of SectionNew
    const sectionsNewRef = useRef(config.sectionsNew ?? config.sections)
    if (mode === 'edit' || mode === 'confirm') { sections = config.sections } else {
        sections = sectionsNewRef.current
    }

    // Prevent submission of Enter except in textareas and contenteditable elements
    const handleEnterKeyDown = (e) => {
        if (e.key === 'Enter' && e.target.tagName.toLowerCase() !== 'textarea' && e.target.contentEditable !== 'true') {
            e.preventDefault()
            //This dispatches all other buttons that the Submit Button which is prevented.
            if (e.target.name && e.target.name !== 'submit') {
                //dispatch(e.target.name)
            }
            if (e.target.name === 'submit') {
                handleSubmit(e)
            }
        }
    }

    useEffect(() => {
        //Updating the time stamp to ensure PDF is refreshed
        setState({ savedOn: new Date() })
    }, [])

    // Renders a single section by its meta.type. Extracted so sections can be
    // rendered either linearly or grouped into tabs (config.tabs).
    const renderSection = (section, i) => {
        if (resolveVisibility(section.meta, { values: state, context: { mode: options?.mode } }) === 'collapse') return null
        return (
            <React.Fragment key={i}>
                {section.meta.type === 'form' ?
                    <CardViewFormSection
                        expanded={expanded[section.id]}
                        toggleExpanded={toggleExpanded}
                        section={section}
                        entity={entity}
                        key={`${section.id}`}
                        dispatch={localDispatch}
                        validated={validated}
                        useStore={useStore}
                        options={options}
                    /> : section.meta.type === 'lines' ?
                        <CardViewFormLines
                            expanded={expanded[section.id]}
                            toggleExpanded={toggleExpanded}
                            section={section}
                            entity={entity}
                            linesArray={section.meta.linesArray}
                            key={`${section.id}`}
                            dispatch={dispatch}
                            validated={validated}
                            useStore={useStore}
                            options={options}
                        /> : section.meta.type === 'totals' ?
                            <CardViewFormTotals
                                section={section}
                                tData={state}
                                key={`${section.id}`}
                            /> : section.meta.type === 'grid' ?
                                <CardViewFormGrid
                                    section={section}
                                    useStore={useStore}
                                    options={options}
                                    key={`${section.id}`}
                                /> : section.meta.type === 'extGrid' ?
                                    <CardViewFormExtGrid
                                        section={section}
                                        options={options}
                                        key={`${section.id}`}
                                    /> : <></>}
            </React.Fragment>
        )
    }

    // Tabs (opt-in): when config.tabs is set, sections without a meta.tab render
    // above the tab bar (e.g. the header), sections are grouped into tabs by
    // meta.tab, and a tab may render a registered extension component instead.
    const tabbed = Array.isArray(config.tabs) && config.tabs.length > 0
    const headerSections = tabbed ? (sections ?? []).filter(s => !s.meta.tab) : (sections ?? [])
    const tabsConfig = tabbed ? config.tabs.map(tab => ({
        key: tab.key,
        label: tab.label,
        icon: tab.icon,
        content: tab.extension && cardExtensions[tab.extension]
            ? React.createElement(cardExtensions[tab.extension])
            : <>{(sections ?? []).filter(s => s.meta.tab === tab.key).map(renderSection)}</>
    })) : []

    //console.log('re-rendering CardViewForm')
    return (
        <>
            <Form id={config.entity} autoComplete='false' name={config.entity} onSubmit={handleSubmit} onKeyDown={handleEnterKeyDown}>
                <input name="hidden" type="text" style={{ display: 'none' }} autoComplete='false' />
                <Form.Shell name="Form-Shell" width={config.width}>
                    {headerSections.map(renderSection)}
                    {tabbed && <Tabs config={tabsConfig} id={`card-${config.entity}`} type="" conditionValue={null} />}
                </Form.Shell>
            </Form>
            {
                config.mobilePanel && <MobilePanel validated={validated}
                    config={config.mobilePanel} useStore={useStore}></MobilePanel>
            }
            {config.extension && cardExtensions[config.extension] &&
                React.createElement(cardExtensions[config.extension])
            }
        </>
    );


})

export default CardViewForm
